import { useEffect } from "react";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { Z } from "../../global/constants/zIndex";
import { useActiveRasterTypename } from "../../hooks/useActiveRasterTypename";
import { geoServerAuthHeader } from "../../services/geoServerAuth";

const GEOSERVER_URL = "https://geoserver-atlas.isitics.com/geoserver";

/**
 * Tiles WMS de workspace protegido.
 *
 * O `WMSTileLayer` padrão carrega cada tile numa tag `<img>`, e `<img>` não
 * aceita cabeçalho `Authorization` — por isso os tiles precisam vir de `fetch`
 * e virar object URL. O preflight CORS do GeoServer libera o header
 * `authorization`, então a requisição autenticada funciona direto do browser.
 */
const AuthenticatedWMS = L.TileLayer.WMS.extend({
  // Declarar `authHeader` como opção é obrigatório, não cosmético: o Leaflet
  // transforma em parâmetro WMS toda chave de options que não pertence a
  // `this.options` (TileLayer.WMS.initialize), e a credencial acabaria
  // serializada na query string de cada tile — indo parar no histórico do
  // browser, no Referer e nos logs do GeoServer e de qualquer proxy.
  options: { authHeader: undefined as string | undefined },

  createTile(
    this: L.TileLayer.WMS & {
      getTileUrl: (coords: L.Coords) => string;
      options: { authHeader?: string };
    },
    coords: L.Coords,
    done: (error: Error | undefined, tile: HTMLImageElement) => void
  ) {
    const tile = document.createElement("img");
    tile.setAttribute("role", "presentation");
    tile.alt = "";

    const { authHeader } = this.options;
    const controller = new AbortController();

    let objectUrl: string | undefined;
    const revoke = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = undefined;
      }
    };

    // Um tile em carregamento é cancelado de duas formas: no zoom, o Leaflet
    // sobrescreve tile.onload/tile.onerror (_abortLoading) sem tocar no
    // <img>; no pan/prune/unmount, ele remove o tile da grade (_removeTile).
    // Nos dois casos quem recebe o aviso é um evento na própria layer, não o
    // <img> — por isso o cancelamento do fetch escuta esses eventos, e não só
    // load/error do tile.
    //
    // `tileabort` só é disparado enquanto o load ainda está em andamento —
    // uma vez que o tile carregou (ou falhou), Leaflet não o aborta mais, e
    // esse listener pode ser removido. `tileunload`, porém, dispara quando o
    // tile *já carregado* sai da grade (pan/zoom/unmount) — é só aí que o
    // blob pode ser liberado com segurança, então esse listener precisa
    // continuar ativo depois do load, não ser removido junto com o outro.
    const onTileAbort = (event: L.TileEvent) => {
      if (event.tile !== tile) return;
      controller.abort();
      revoke();
      cleanupAll();
    };
    const onTileUnload = (event: L.TileEvent) => {
      if (event.tile !== tile) return;
      controller.abort();
      revoke();
      cleanupAll();
    };
    const cleanupAbortListener = () => {
      this.off("tileabort", onTileAbort);
    };
    const cleanupAll = () => {
      cleanupAbortListener();
      this.off("tileunload", onTileUnload);
    };
    this.on("tileabort", onTileAbort);
    this.on("tileunload", onTileUnload);

    // addEventListener, não atribuição direta: _abortLoading troca
    // tile.onload/tile.onerror por um no-op, mas um listener registrado
    // assim continua sendo entregue mesmo depois disso.
    tile.addEventListener("load", () => {
      cleanupAbortListener();
      done(undefined, tile);
    });
    tile.addEventListener("error", () => {
      cleanupAbortListener();
      done(new Error("Falha ao decodificar o tile"), tile);
    });

    fetch(this.getTileUrl(coords), {
      headers: authHeader ? { Authorization: authHeader } : undefined,
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`GeoServer respondeu ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        // O object URL só pode ser liberado depois que o browser decodificou a
        // imagem e o tile sair de uso (onTileUnload acima) — revogar aqui
        // quebraria a imagem assim que ela terminasse de carregar.
        objectUrl = URL.createObjectURL(blob);
        tile.src = objectUrl;
      })
      .catch((error: Error) => {
        if (error.name === "AbortError") return;
        cleanupAll();
        done(error, tile);
      });

    return tile;
  },
});

interface AuthenticatedTilesProps {
  /** Workspace protegido — define a credencial e o endpoint WMS. */
  workspace: string;
  /** Parâmetros WMS (layers, format, sld_body, dimensões...). */
  params: Record<string, unknown>;
  opacity?: number;
  zIndex?: number;
  /**
   * Muda quando os parâmetros mudam. O TileLayer do Leaflet não redesenha ao
   * mutar params, então a camada é recriada a cada valor novo.
   */
  reloadKey: string;
}

/**
 * Camada de tiles autenticada, parametrizável. Use quando for preciso passar
 * `sld_body` ou dimensões junto; para o caso simples prefira
 * `AuthenticatedWMSTileLayer`.
 */
export function AuthenticatedTiles({
  workspace,
  params,
  opacity = 0.8,
  zIndex = Z.MAP_LAYER_DEFAULT,
  reloadKey,
}: AuthenticatedTilesProps) {
  const map = useMap();

  useEffect(() => {
    const layer = new (AuthenticatedWMS as any)(
      `${GEOSERVER_URL}/${workspace}/wms`,
      {
        format: "image/png",
        transparent: true,
        version: "1.1.0",
        tiled: true,
        opacity,
        zIndex,
        authHeader: geoServerAuthHeader(workspace),
        ...params,
      }
    ) as L.TileLayer.WMS;

    layer.addTo(map);
    return () => {
      map.removeLayer(layer);
    };
    // `params` é recriado a cada render; `reloadKey` resume o que de fato mudou.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, workspace, opacity, zIndex, reloadKey]);

  return null;
}

interface AuthenticatedWMSTileLayerProps {
  typename: string;
  workspace: string;
  zIndex?: number;
  opacity?: number;
  detailedLayerName?: string;
  detailedLayerZoomThreshold?: number;
}

export function AuthenticatedWMSTileLayer({
  typename,
  workspace,
  zIndex,
  opacity,
  detailedLayerName,
  detailedLayerZoomThreshold = 12,
}: AuthenticatedWMSTileLayerProps) {
  const activeTypename = useActiveRasterTypename(
    typename,
    detailedLayerName,
    detailedLayerZoomThreshold
  );

  return (
    <AuthenticatedTiles
      workspace={workspace}
      params={{ layers: `${workspace}:${activeTypename}` }}
      opacity={opacity}
      zIndex={zIndex}
      reloadKey={activeTypename}
    />
  );
}

export default AuthenticatedWMSTileLayer;
